'use client';

import { useCallback, useEffect } from 'react';
import { getAuthQueryParam, isLoggedInUser } from '@pixum/auth-core-ui';
import { AuthDialog } from 'components/auth';
import { hideTrustedShopBadge, hideTrustPilotBadge, PixumEnvUtils } from 'utils';
import type { I18nConstants } from 'components/types';
import { useUIStore } from 'stores/ui';

type AuthDialogControllerProps = {
  authI18n: I18nConstants | undefined;
};

export function AuthDialogController({ authI18n }: AuthDialogControllerProps) {
  const { isAuthUIVisible, setAuthUIVisibility } = useUIStore(state => state);

  const isUserLoggedIn = isLoggedInUser();
  const authParam = getAuthQueryParam();

  useEffect(() => {
    if (!authI18n) return;
    const authProps = PixumEnvUtils.get('AUTH_PROPS');

    if (authProps?.I18N) {
      PixumEnvUtils.update('AUTH_PROPS', { I18N: { authModule: authI18n } });
    }
  }, [authI18n]);

  const onAuthUIClose = useCallback(() => {
    setAuthUIVisibility(false);
  }, [setAuthUIVisibility]);

  // AuthDialog already calls router.refresh() and counts the login internally in its handleAuthSuccess.
  // This callback only handles local side-effects to avoid a double refresh that
  // races on mobile and can leave AccountSectionContent with a stale isAuthenticated prop.
  const onAuthSuccess = useCallback(() => {
    if (authParam) {
      history.replaceState(null, '', window.location.pathname);
    }
  }, [authParam]);

  useEffect(() => {
    if (!isUserLoggedIn && !!authParam) {
      setAuthUIVisibility(true);
    }
  }, [isUserLoggedIn, authParam, setAuthUIVisibility]);

  useEffect(() => {
    if (isAuthUIVisible && !isUserLoggedIn) {
      hideTrustedShopBadge(true);
      hideTrustPilotBadge(true);
    } else {
      hideTrustedShopBadge(false);
      hideTrustPilotBadge(false);
    }
  }, [isAuthUIVisible, isUserLoggedIn]);

  if (!isUserLoggedIn && isAuthUIVisible) {
    return (
      <AuthDialog
        isOpen={isAuthUIVisible}
        onClose={onAuthUIClose}
        onSuccess={onAuthSuccess}
        showSidePanel
      />
    );
  }

  return null;
}
